'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Search, BookOpen, PenTool, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { localizedPublicPath, stripPublicLocale } from '@/i18n/config';
import { getLanguageDataByCode } from '@/i18n/language-data';

type BottomNavItem = {
  route: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

export default function BottomNav({ languageCode }: { languageCode: string }) {
  const pathname = usePathname();
  const basePathname = stripPublicLocale(pathname);
  const langData = getLanguageDataByCode(languageCode);

  if (pathname.startsWith('/chat/')) return null;

  const items: BottomNavItem[] = [
    { route: localizedPublicPath('/discover', languageCode), icon: Search, label: langData.nav_discover },
    { route: '/puzzles', icon: BookOpen, label: langData.nav_my_puzzles },
    { route: '/create', icon: PenTool, label: langData.nav_create },
    { route: '/profile', icon: User, label: langData.nav_profile },
  ];

  return (
    <nav
      className={cn(
        'mobile-bottom-nav fixed bottom-0 left-0 right-0 z-40 flex items-stretch lg:hidden',
        'border-t border-[var(--sidebar-border)] bg-[var(--sidebar)]',
      )}
      style={{
        height: 'var(--mobile-bottom-nav-height)',
        paddingBottom: 'var(--safe-area-bottom)',
      }}
    >
      {items.map((item) => {
        const itemBaseRoute = stripPublicLocale(item.route);
        const isActive = basePathname === itemBaseRoute || basePathname.startsWith(itemBaseRoute + '/');
        const Icon = item.icon;
        return (
          <Link
            key={item.route}
            href={item.route}
            className={cn(
              'relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
              isActive ? 'text-[var(--lamp)]' : 'text-gray-500 hover:text-gray-300',
            )}
          >
            {isActive && (
              <span className="absolute top-0 left-1/2 h-[2px] w-8 -translate-x-1/2 rounded-full bg-[var(--lamp)]" />
            )}
            <Icon className="h-[20px] w-[20px]" />
            <span
              className="text-[10px] tracking-[0.5px] truncate max-w-[68px]"
              style={{ fontFamily: 'var(--font-noto), "Noto Sans SC", sans-serif' }}
            >
              {item.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
